"use client";

import { useState } from "react";
import { useRfqBasket } from "./RfqBasketContext";
import {
  X,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  ArrowRight,
  CheckCircle2,
  Send,
  Package,
} from "lucide-react";

export default function RfqBasketDrawer() {
  const { items, isOpen, closeBasket, removeItem, updateQuantity, updateRemarks, clearBasket } =
    useRfqBasket();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    whatsapp: "",
    country: "",
    target_price: "",
    delivery_time: "",
    requirements: "",
  });

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) return;

    setSubmitting(true);
    try {
      const res = await fetch("/api/rfq", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          items: items.map((i) => ({
            slug: i.slug,
            sku: i.sku,
            name: i.name,
            category: i.category,
            quantity: i.quantity,
            remarks: i.remarks,
            target_price: i.targetPrice,
          })),
        }),
      });

      if (res.ok) {
        setSubmitted(true);
        clearBasket();
      }
    } catch (err) {
      console.error("RFQ submit error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeBasket}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-[var(--color-surface)] border-l border-[var(--color-border)] shadow-2xl transition-transform duration-300 flex flex-col ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[var(--color-border)] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[var(--color-primary)]/10 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-[var(--color-primary)]" />
            </div>
            <div>
              <h2 className="font-semibold text-lg">RFQ Basket</h2>
              <p className="text-xs text-[var(--color-muted)]">{totalItems} items</p>
            </div>
          </div>
          <button
            onClick={closeBasket}
            className="p-2 rounded-lg hover:bg-[var(--color-surface-light)] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {submitted ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-[var(--color-primary)]" />
              </div>
              <h3 className="text-xl font-semibold mb-2">RFQ Submitted!</h3>
              <p className="text-[var(--color-muted)] text-sm mb-6">
                Thank you for your inquiry. Our sales team will review your 
                request and send a detailed quote within 24 hours.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setShowForm(false);
                  closeBasket();
                }}
                className="px-6 py-2.5 rounded-xl bg-[var(--color-primary)] text-[var(--color-background)] font-medium"
              >
                Close
              </button>
            </div>
          ) : showForm ? (
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-sm text-[var(--color-muted)] hover:text-[var(--color-primary)] flex items-center gap-1 mb-2"
              >
                ← Back to basket
              </button>
              <h3 className="font-semibold text-lg">Submit RFQ</h3>

              <label className="block">
                <span className="block text-sm font-medium mb-1.5">Company Name *</span>
                <input
                  type="text"
                  required
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none text-sm"
                />
              </label>

              <label className="block">
                <span className="block text-sm font-medium mb-1.5">Contact Name *</span>
                <input
                  type="text"
                  required
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  className="w-full px-4 py-2.5 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none text-sm"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-sm font-medium mb-1.5">Email *</span>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none text-sm"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium mb-1.5">Phone</span>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none text-sm"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-sm font-medium mb-1.5">WhatsApp</span>
                  <input
                    type="tel"
                    value={formData.whatsapp}
                    onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none text-sm"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium mb-1.5">Country</span>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none text-sm"
                  />
                </label>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-sm font-medium mb-1.5">Target Price</span>
                  <input
                    type="text"
                    value={formData.target_price}
                    onChange={(e) => setFormData({ ...formData, target_price: e.target.value })}
                    placeholder="e.g. $5,000"
                    className="w-full px-4 py-2.5 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none text-sm"
                  />
                </label>
                <label className="block">
                  <span className="block text-sm font-medium mb-1.5">Delivery Time</span>
                  <input
                    type="text"
                    value={formData.delivery_time}
                    onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
                    placeholder="e.g. 15 days"
                    className="w-full px-4 py-2.5 rounded-xl bg-[var(--color-background)] border border-[var(--color-primary)]/30 focus:border-[var(--color-primary)] focus:outline-none text-sm"
                  />
                </label>
              </div>

              <label className="block">
                <span className="block text-sm font-medium mb-1.5">Additional Requirements</span>
                <textarea
                  rows={3}
                  value={formData.requirements}
                  onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                  placeholder="Tell us about your specific requirements, certifications, packaging, etc."
                  className="w-full px-4 py-2.5 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)] focus:border-[var(--color-primary)] focus:outline-none text-sm resize-none"
                />
              </label>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] text-[var(--color-background)] font-semibold hover:shadow-lg hover:shadow-[var(--color-glow-soft)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Submitting..." : "Submit RFQ"}
              </button>
            </form>
          ) : items.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[var(--color-surface-light)] flex items-center justify-center">
                <Package className="w-8 h-8 text-[var(--color-muted)]/50" />
              </div>
              <h3 className="font-semibold mb-2">Your basket is empty</h3>
              <p className="text-sm text-[var(--color-muted)]">
                Browse our product catalog and add items to your RFQ basket.
              </p>
            </div>
          ) : (
            <div className="p-5 space-y-3">
              {items.map((item) => (
                <div
                  key={item.slug}
                  className="p-4 rounded-xl bg-[var(--color-background)] border border-[var(--color-border)]"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs text-[var(--color-primary)] mb-1">
                        {item.category}
                      </div>
                      <h4 className="font-medium text-sm leading-snug line-clamp-2">
                        {item.name}
                      </h4>
                      <div className="text-xs text-[var(--color-muted)] mt-1">
                        SKU: {item.sku}
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(item.slug)}
                      className="p-1.5 rounded-lg hover:bg-red-500/10 text-[var(--color-muted)] hover:text-red-400 transition-colors shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center rounded-lg border border-[var(--color-border)] overflow-hidden">
                      <button
                        onClick={() => updateQuantity(item.slug, item.quantity - 1)}
                        className="p-1.5 hover:bg-[var(--color-surface-light)] transition-colors"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          updateQuantity(item.slug, Math.max(1, parseInt(e.target.value) || 1))
                        }
                        className="w-12 text-center bg-transparent text-sm focus:outline-none"
                      />
                      <button
                        onClick={() => updateQuantity(item.slug, item.quantity + 1)}
                        className="p-1.5 hover:bg-[var(--color-surface-light)] transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    {item.priceRange && (
                      <span className="text-sm font-semibold text-[var(--color-primary)]">
                        {item.priceRange.split(" - ")[0]}
                      </span>
                    )}
                  </div>

                  <div className="mt-3">
                    <input
                      type="text"
                      value={item.remarks || ""}
                      onChange={(e) => updateRemarks(item.slug, e.target.value)}
                      placeholder="Remarks (e.g. specific model, custom spec)..."
                      className="w-full px-3 py-2 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-xs focus:border-[var(--color-primary)] focus:outline-none"
                    />
                  </div>
                </div>
              ))}

              {items.length > 0 && (
                <button
                  onClick={clearBasket}
                  className="w-full text-xs text-[var(--color-muted)] hover:text-red-400 transition-colors py-2"
                >
                  Clear basket
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!showForm && !submitted && items.length > 0 && (
          <div className="p-5 border-t border-[var(--color-border)] shrink-0">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-[var(--color-muted)]">Total Items</span>
              <span className="font-bold text-lg">{totalItems}</span>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="w-full py-3 rounded-xl bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] text-[var(--color-background)] font-semibold hover:shadow-lg hover:shadow-[var(--color-glow-soft)] transition-all flex items-center justify-center gap-2"
            >
              Submit RFQ
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </>
  );
}
